/**
 * Sanction Product Chat Widget v2
 *
 * Lightweight storefront chat powered by the Product Tools backend.
 * Streams responses via SSE — no client-side AI, no prompt engineering.
 *
 * Modules:
 *   SessionManager      — visitor/session ID persistence
 *   ChatAPI             — backend communication (fetch + SSE parsing)
 *   CartLinkTracker     — records cart_token → session on /cart/add success
 *   ChatWidget          — DOM rendering, user interaction, message display
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------
  const CHAT_CONFIG = {
    apiUrl: 'https://product-tools.sanctionsnow.com',
    // apiUrl: 'http://localhost:3000', // Dev
  };

  // ---------------------------------------------------------------------------
  // Module 1: SessionManager
  // ---------------------------------------------------------------------------
  const SessionManager = {
    visitorId: null,
    sessionId: null,

    init() {
      this.visitorId = localStorage.getItem('sanction_chat_visitor');
      if (!this.visitorId) {
        this.visitorId = crypto.randomUUID();
        localStorage.setItem('sanction_chat_visitor', this.visitorId);
      }
      this.sessionId = sessionStorage.getItem('sanction_chat_session');
    },

    setSession(id) {
      this.sessionId = id;
      sessionStorage.setItem('sanction_chat_session', id);
    },

    clearSession() {
      this.sessionId = null;
      sessionStorage.removeItem('sanction_chat_session');
    },
  };

  // ---------------------------------------------------------------------------
  // Module 2: ChatAPI
  // ---------------------------------------------------------------------------
  const ChatAPI = {
    async createSession(productData, options) {
      var body = Object.assign({}, productData, options && options.forceNew ? { forceNew: true } : null);
      const res = await fetch(CHAT_CONFIG.apiUrl + '/api/chat/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to create session');
      return res.json();
    },

    async getSuggestions(sessionId) {
      const res = await fetch(
        CHAT_CONFIG.apiUrl + '/api/chat/sessions/' + sessionId + '/suggestions'
      );
      if (!res.ok) return { questions: [] };
      return res.json();
    },

    async resumeSession(sessionId, visitorId) {
      const res = await fetch(
        CHAT_CONFIG.apiUrl +
          '/api/chat/sessions/' +
          sessionId +
          '?visitorId=' +
          encodeURIComponent(visitorId)
      );
      if (!res.ok) return null;
      return res.json();
    },

    /**
     * Send a message and stream the response via SSE.
     *
     * @param {string}   sessionId
     * @param {string}   content   - user message
     * @param {string}   source    - 'typed' | 'suggested'
     * @param {Function} onToken   - called with each text token
     * @param {Function} onDone    - called when stream completes
     * @param {Function} onError   - called on error
     */
    async sendMessage(sessionId, content, source, onToken, onDone, onError) {
      try {
        const res = await fetch(
          CHAT_CONFIG.apiUrl + '/api/chat/sessions/' + sessionId + '/messages',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content, source }),
          }
        );

        if (!res.ok) {
          var err = {};
          try { err = await res.json(); } catch (_) { /* ignore */ }
          onError({ message: err.error || 'Something went wrong. Try again.' });
          return;
        }

        var reader = res.body.getReader();
        var decoder = new TextDecoder();
        var buffer = '';

        while (true) {
          var result = await reader.read();
          if (result.done) break;

          buffer += decoder.decode(result.value, { stream: true });
          var lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete line in buffer

          for (var i = 0; i < lines.length; i++) {
            var line = lines[i];
            if (!line.startsWith('data: ')) continue;
            try {
              var data = JSON.parse(line.slice(6));
              if (data.type === 'token') onToken(data.content);
              else if (data.type === 'done') onDone(data);
              else if (data.type === 'error') onError(data);
            } catch (_) {
              // Skip malformed SSE lines
            }
          }
        }

        // Flush remaining buffer
        if (buffer.startsWith('data: ')) {
          try {
            var lastData = JSON.parse(buffer.slice(6));
            if (lastData.type === 'token') onToken(lastData.content);
            else if (lastData.type === 'done') onDone(lastData);
            else if (lastData.type === 'error') onError(lastData);
          } catch (_) { /* ignore */ }
        }
      } catch (_) {
        onError({ message: 'Connection lost. Please try again.' });
      }
    },
  };

  // ---------------------------------------------------------------------------
  // Module 3: CartLinkTracker
  // ---------------------------------------------------------------------------
  // On /cart/add success, extracts the exact line item(s) from the response
  // body (Shopify's Ajax Cart API returns the added items authoritatively) and
  // POSTs one cart-link per item. /cart.js is only used to obtain the cart
  // token. No DOM scraping, no guessing from cart ordering.
  //
  // Covers fetch only — Sanction's Dawn-family theme uses fetch exclusively
  // for /cart/add. Legacy themes using XHR or form-submit will fall through
  // to the webhook's add_to_cart fallback path.
  // ---------------------------------------------------------------------------
  const CartLinkTracker = {
    initialized: false,

    init: function () {
      if (this.initialized) return;
      this.initialized = true;

      var originalFetch = window.fetch;
      window.fetch = function (resource, init) {
        var url = (typeof resource === 'string')
          ? resource
          : (resource && resource.url ? resource.url : '');

        var result = originalFetch.apply(this, arguments);

        if (url && url.indexOf('/cart/add') !== -1) {
          // Snapshot sessionId at fire time so a concurrent clearSession()
          // (e.g. "New Chat" click) can't misattribute to the new session.
          var capturedSessionId = SessionManager.sessionId;

          Promise.resolve(result).then(function (response) {
            if (!response || !response.ok || !capturedSessionId) return;

            // Clone before reading — the theme may also consume the body.
            var cloned;
            try { cloned = response.clone(); } catch (_) { return; }

            cloned.json().then(function (data) {
              CartLinkTracker.recordLink(capturedSessionId, data).catch(function () { /* swallow */ });
            }, function () { /* non-JSON response, skip */ });
          }, function () { /* swallow fetch errors */ });
        }

        return result;
      };
    },

    recordLink: async function (sessionId, addResponseData) {
      if (!sessionId || !addResponseData) return;

      // /cart/add.js returns either a single item ({variant_id, ...}) or
      // a multi-item payload ({items: [{...}, {...}]}) for bundle adds.
      var items;
      if (Array.isArray(addResponseData.items) && addResponseData.items.length > 0) {
        items = addResponseData.items;
      } else if (addResponseData.variant_id) {
        items = [addResponseData];
      } else {
        return;
      }

      // Fetch cart_token once and reuse for all items.
      var cartToken = null;
      try {
        var cartResp = await fetch('/cart.js');
        if (!cartResp.ok) return;
        var cart = await cartResp.json();
        // /cart.js returns "<token>?key=<hex>" but the orders webhook sends
        // the bare "<token>" — strip the suffix so attribution can match.
        cartToken = cart && cart.token && String(cart.token).split('?')[0];
      } catch (_) { return; }

      if (!cartToken) return;

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (!item || !item.product_id || !item.variant_id) continue;
        await CartLinkTracker._postLinkWithRetry(cartToken, sessionId, item);
      }
    },

    _postLinkWithRetry: async function (cartToken, sessionId, item) {
      var payload = {
        cartToken: cartToken,
        sessionId: sessionId,
        productId: String(item.product_id),
        variantId: String(item.variant_id),
        price: (item.price || 0) / 100,
        quantity: item.quantity || 1,
      };

      // One retry after 2s for ANY transient failure:
      //   - 400: user's first message may still be in flight (engagement race)
      //   - 429: rate-limit spike
      //   - 5xx: transient server/infra error
      //   - thrown error: network drop
      // Real validation failures (session/product errors) still produce 400
      // twice, so after 2 attempts we give up — bounded cost per cart-add.
      for (var attempt = 0; attempt < 2; attempt++) {
        var shouldRetry = false;
        try {
          var resp = await fetch(CHAT_CONFIG.apiUrl + '/api/chat/cart-link', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (resp.ok) return;
          // Any non-2xx is a candidate for one retry
          shouldRetry = attempt < 1;
        } catch (_) {
          // Network error / CORS / offline
          shouldRetry = attempt < 1;
        }

        if (!shouldRetry) return;
        await new Promise(function (r) { setTimeout(r, 2000); });
      }
    },
  };

  // ---------------------------------------------------------------------------
  // Module 4: ChatWidget
  // ---------------------------------------------------------------------------
  var ChatWidget = {
    els: {},
    isStreaming: false,
    activated: false, // flipped on first user engagement; guards lazy init
    streamedText: '', // accumulates raw text for the current assistant response

    init: function () {
      this.els = {
        container: document.querySelector('.product-chat-container'),
        messages: document.getElementById('chatMessages'),
        input: document.getElementById('chatInput'),
        sendBtn: document.getElementById('sendMessage'),
        prompts: document.getElementById('suggestedPrompts'),
        promptsLoader: document.getElementById('promptsLoader'),
        promptsWrapper: document.querySelector('.suggested-prompts-wrapper'),
        disclaimer: document.getElementById('aiDisclaimer'),
        toolbar: document.getElementById('chatToolbar'),
        newChatBtn: document.getElementById('newChatBtn'),
      };

      if (!this.els.container) return;

      var self = this;

      // Wire up send button
      if (this.els.sendBtn) {
        this.els.sendBtn.addEventListener('click', function () { self.handleSend(); });
      }

      // Enter key sends message
      if (this.els.input) {
        this.els.input.addEventListener('keypress', function (e) {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            self.handleSend();
          }
        });
      }

      // Wire up new-chat button
      if (this.els.newChatBtn) {
        this.els.newChatBtn.addEventListener('click', function () {
          if (!self.isStreaming) self.startNewChat();
        });
      }

      SessionManager.init();

      // Keep page-load cost at zero: silently resume an existing conversation
      // if one lives in sessionStorage for THIS product, otherwise do nothing.
      // No session creation, no AI suggestion call — those wait for accordion open.
      this.silentResume();

      // The widget renders inside Shopify's "Ask Questions" accordion
      // (<details class="disclosure">), closed by default. Match v1's
      // behavior: activate only when the user actually opens the accordion.
      // If the widget is rendered outside an accordion (preview / embed),
      // we fall back to immediate activation so the chat is usable.
      var activate = function () { self.activate(); };
      var parentDetails = this.els.container.closest('details');
      if (parentDetails) {
        if (parentDetails.open) {
          // Already open on page load (theme config or browser restored state) —
          // treat this the same as a user opening it. Still only fires once.
          activate();
        } else {
          parentDetails.addEventListener('toggle', function () {
            if (parentDetails.open) activate();
          });
        }
      } else {
        // Widget rendered standalone (no accordion). Fall back to input focus
        // so the AI call still waits for user intent rather than firing on
        // every page load like the broken auto-fire flow.
        if (this.els.input) {
          this.els.input.addEventListener('focus', activate, { once: true });
          this.els.input.addEventListener('pointerdown', activate, { once: true });
        }
      }
    },

    // Silent resume on page load. Restores an existing conversation if the
    // tab-scoped session ID in sessionStorage matches THIS product. Cheap:
    // one DB lookup, no AI. If messages exist, renders them and marks the
    // widget as already-activated so the next focus doesn't re-fetch.
    silentResume: async function () {
      if (!SessionManager.sessionId) return;
      var productHandle = getProductHandle();
      try {
        var resumed = await ChatAPI.resumeSession(
          SessionManager.sessionId,
          SessionManager.visitorId
        );
        if (
          resumed &&
          resumed.session &&
          resumed.session.product_handle === productHandle
        ) {
          var msgs = resumed.messages || [];
          if (msgs.length > 0) {
            this.renderHistory(msgs);
            CartLinkTracker.init();
            this.activated = true;
          }
          // Resumed empty session — keep the ID, skip suggestions until engage.
          return;
        }
        // Product mismatch: clear so the next activation creates a fresh session.
        SessionManager.clearSession();
      } catch (_) {
        // Silent fail — next activation will recreate cleanly.
      }
    },

    // First-engagement entry point. Creates a session (if none) and loads
    // the suggestion pills. Idempotent — repeat calls are no-ops. Shows a
    // subtle preloader so the user isn't staring at an empty pill row.
    activate: async function () {
      if (this.activated || this.isStreaming) return;
      this.activated = true;

      var wrapper = this.els.promptsWrapper;
      // is-activating reserves 60px of min-height so the wrapper animates
      // open smoothly instead of popping from 0 to 60. Kept through the
      // entire activation — only dropped once pills are visible and have
      // pushed past the 60px floor on their own.
      if (wrapper) wrapper.classList.add('is-activating');
      this.showPromptsLoader();

      try {
        var sessionId = SessionManager.sessionId || await this.ensureSession();
        if (!sessionId) {
          this.hidePromptsLoader();
          if (wrapper) wrapper.classList.remove('is-activating');
          return;
        }
        await this.loadSuggestions(sessionId, { silent: false });
      } catch (_) {
        this.hidePromptsLoader();
        if (wrapper) wrapper.classList.remove('is-activating');
      }
      // is-activating is dropped by loadSuggestions() once pills are
      // rendered and the first stagger frame has fired — see there.
    },

    // Create a session without fetching suggestions. Used by handleSend()
    // when a user types + sends before the focus handler has fired.
    ensureSession: async function () {
      var productData = {
        visitorId: SessionManager.visitorId,
        productId: getProductId(),
        productHandle: getProductHandle(),
        variantId: this.els.container.dataset.variantId || '',
        pageUrl: window.location.href,
      };
      try {
        var result = await ChatAPI.createSession(productData);
        if (result && result.sessionId) {
          SessionManager.setSession(result.sessionId);
          return result.sessionId;
        }
      } catch (_) {}
      return null;
    },

    showPromptsLoader: function () {
      var loader = this.els.promptsLoader;
      if (!loader) return;
      loader.style.display = 'flex';
      loader.style.opacity = '0';
      // Force reflow, then fade in — the opacity transition is 300ms via CSS.
      void loader.offsetHeight;
      loader.style.opacity = '1';
    },
    hidePromptsLoader: function () {
      var loader = this.els.promptsLoader;
      if (!loader) return;
      loader.style.opacity = '0';
      setTimeout(function () {
        if (loader.style.opacity === '0') loader.style.display = 'none';
      }, 300);
    },

    startNewChat: async function () {
      var self = this;

      // Keep the prompts wrapper hidden while we populate it behind the
      // scenes — we'll reveal it in one clean fade once everything's ready.
      if (this.els.promptsWrapper) this.els.promptsWrapper.style.display = 'none';
      if (this.els.prompts) {
        this.els.prompts.classList.remove('visible');
        this.els.prompts.innerHTML = '';
      }

      // Reset state: "new chat" is an explicit user action, so we skip lazy
      // activation guards and go straight to create session + load suggestions.
      SessionManager.clearSession();
      this.activated = false;
      this.showPromptsLoader();

      var sessionReady = (async function () {
        var sessionId = await self.ensureSession();
        if (!sessionId) return;
        await self.loadSuggestions(sessionId, { silent: true });
      })();

      // Fade + collapse the existing conversation (320ms)
      if (this.els.messages) this.els.messages.classList.add('is-clearing');
      if (this.els.toolbar) this.els.toolbar.classList.add('is-exiting');
      if (this.els.disclaimer) this.els.disclaimer.classList.remove('visible');

      // Wait until BOTH the fade-out is done AND the new session / prompts
      // are populated before revealing anything.
      await Promise.all([
        new Promise(function (r) { setTimeout(r, 320); }),
        sessionReady,
      ]);

      // Tear down the old conversation
      if (this.els.messages) {
        this.els.messages.innerHTML = '';
        this.els.messages.style.display = 'none';
        this.els.messages.classList.remove('is-clearing');
      }
      this.setToolbarVisible(false);
      if (this.els.toolbar) this.els.toolbar.classList.remove('is-exiting');

      this.hidePromptsLoader();
      this.activated = true;

      // Reveal the fully-populated prompts wrapper with one smooth fade-in.
      // Individual prompt buttons run their own staggered entrance animation.
      if (this.els.promptsWrapper) {
        this.els.promptsWrapper.style.display = '';
        this.els.promptsWrapper.classList.add('is-entering');
        setTimeout(function () {
          if (self.els.promptsWrapper) self.els.promptsWrapper.classList.remove('is-entering');
        }, 420);
      }
    },

    setToolbarVisible: function (visible) {
      if (!this.els.toolbar) return;
      this.els.toolbar.classList.toggle('is-visible', !!visible);
      this.els.toolbar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    },

    loadSuggestions: async function (sessionId, opts) {
      if (!this.els.prompts) return;
      var silent = !!(opts && opts.silent);

      // Show loader (skipped in silent mode — caller wants to reveal only
      // when prompts are fully populated, no spinner flicker). activate()
      // already shows the loader, so this is a belt-and-suspenders for
      // paths that call loadSuggestions directly.
      if (!silent) this.showPromptsLoader();

      var self = this;
      var wrapper = this.els.promptsWrapper;

      try {
        var data = await ChatAPI.getSuggestions(sessionId);
        var questions = data.questions;
        if (!questions || questions.length === 0) {
          this.hidePromptsLoader();
          if (wrapper) wrapper.classList.remove('is-activating');
          return;
        }

        // Populate pills while loader is still visible (it's absolutely
        // positioned, so pills expand the wrapper underneath without the
        // loader moving). Then cross-fade: loader fades out AS the pills
        // stagger-fade in — no layout jolt, no visible pop.
        this.els.prompts.innerHTML = '';
        questions.forEach(function (q) {
          var btn = document.createElement('button');
          btn.className = 'prompt-button';
          btn.type = 'button';
          btn.textContent = q;
          btn.addEventListener('click', function () {
            self.els.input.value = q;
            self.handleSend('suggested');
          });
          self.els.prompts.appendChild(btn);
        });

        // Kick the pills' entrance one frame AFTER the DOM has the new
        // nodes. Double-rAF ensures layout + initial opacity are committed
        // before the stagger animation starts, otherwise the first pill
        // can skip its fade-in on fast hardware.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            // The snippet's HTML ships #suggestedPrompts with
            // style="opacity: 0" so the empty container doesn't flash
            // before pills arrive. Inline style wins over the .visible
            // class, so we clear it explicitly here — without this, the
            // pills render into the DOM but the whole container stays
            // invisible (the bug Adam caught).
            self.els.prompts.classList.add('visible');
            self.els.prompts.style.opacity = '1';
            self.hidePromptsLoader();
            // Drop min-height once pills have pushed past it. The
            // transition keeps the wrapper smooth as we go from the
            // reserved 60px floor to whatever the pills actually need.
            if (wrapper) {
              setTimeout(function () { wrapper.classList.remove('is-activating'); }, 260);
            }
          });
        });
      } catch (_) {
        this.hidePromptsLoader();
        if (wrapper) wrapper.classList.remove('is-activating');
      }
    },

    handleSend: async function (source) {
      source = source || 'typed';
      var content = this.els.input.value.trim();
      if (!content || this.isStreaming) return;

      // Lazy-activate guards: if the user typed + hit send before the focus
      // handler fired (keyboard-nav users, autocomplete, etc.), we still need
      // a session before streaming can start.
      this.activated = true;
      if (!SessionManager.sessionId) {
        var sessionId = await this.ensureSession();
        if (!sessionId) return;
      }

      // Arm the cart-link hooks now that the visitor is actually chatting.
      // Idempotent — CartLinkTracker.init() guards with this.initialized so
      // subsequent sends are no-ops. Doing this here (not on page load) keeps
      // cart_link rows out of visits where no chat happened.
      CartLinkTracker.init();

      // Fire Chat Used once per page view, on the first real send.
      // Widget is always visible so there's no "open" action to track —
      // sending a message is the meaningful engagement signal.
      if (!this._chatUsedReported) {
        this._chatUsedReported = true;
        if (window.plausible) {
          var container = this.els.container;
          var title = (container && container.dataset.productTitle) || 'unknown';
          var type = (container && container.dataset.productType) || 'unknown';
          plausible('Chat Used', {
            props: {
              source: source,
              product: title.slice(0, 80),
              category: type
            }
          });
        }
        try { sessionStorage.setItem('sanction_used_chat', 'yes'); } catch (e) {}
      }

      this.els.input.value = '';
      this.setInputState(false);
      this.isStreaming = true;

      // Hide suggestions
      if (this.els.promptsWrapper) this.els.promptsWrapper.style.display = 'none';

      // Show messages area and disclaimer
      if (this.els.messages) this.els.messages.style.display = 'block';
      if (this.els.disclaimer) this.els.disclaimer.classList.add('visible');

      // Render user message
      this.appendMessage('user', content);

      // Show typing indicator
      var typingEl = this.showTypingIndicator();

      // Assistant bubble created on first token (not before, to avoid empty blue artifact)
      var assistantBubble = null;
      this.streamedText = '';

      var self = this;

      await ChatAPI.sendMessage(
        SessionManager.sessionId,
        content,
        source,
        // onToken
        function (token) {
          if (typingEl && typingEl.parentNode) typingEl.remove();
          if (!assistantBubble) assistantBubble = self.createAssistantBubble();
          self.streamedText += token;
          // Render accumulated text as formatted HTML
          assistantBubble.innerHTML = formatResponse(self.streamedText);
          self.scrollToBottom();
        },
        // onDone
        function () {
          if (typingEl && typingEl.parentNode) typingEl.remove();
          if (!assistantBubble) assistantBubble = self.createAssistantBubble();
          // Final render of complete text
          assistantBubble.innerHTML = formatResponse(self.streamedText);
          self.isStreaming = false;
          self.setInputState(true);
          self.els.input.focus();
          self.scrollToBottom();
        },
        // onError
        function (err) {
          if (typingEl && typingEl.parentNode) typingEl.remove();
          if (!assistantBubble) assistantBubble = self.createAssistantBubble();
          assistantBubble.textContent =
            err.message || 'Something went wrong. Please try again.';
          assistantBubble.classList.add('chat-error');
          self.isStreaming = false;
          self.setInputState(true);
        }
      );
    },

    // ---- DOM helpers -------------------------------------------------------

    appendMessage: function (role, content) {
      var wrapper = document.createElement('div');
      wrapper.className = 'chat-message chat-message-' + role;

      var bubble = document.createElement('div');
      bubble.className = 'message-content';

      if (role === 'user') {
        bubble.textContent = content;
      } else {
        bubble.innerHTML = formatResponse(content);
      }

      wrapper.appendChild(bubble);
      this.els.messages.appendChild(wrapper);
      this.setToolbarVisible(true);
      this.scrollToBottom();
    },

    createAssistantBubble: function () {
      var wrapper = document.createElement('div');
      wrapper.className = 'chat-message chat-message-assistant';

      var bubble = document.createElement('div');
      bubble.className = 'message-content';

      wrapper.appendChild(bubble);
      this.els.messages.appendChild(wrapper);
      return bubble;
    },

    renderHistory: function (messages) {
      if (!this.els.messages) return;
      this.els.messages.innerHTML = '';

      if (messages.length > 0) {
        this.els.messages.style.display = 'block';
        if (this.els.disclaimer) this.els.disclaimer.classList.add('visible');
        // Hide the suggested-prompts slot entirely — otherwise it reserves
        // empty vertical space above the conversation.
        if (this.els.promptsWrapper) this.els.promptsWrapper.style.display = 'none';
        this.setToolbarVisible(true);
      } else {
        this.setToolbarVisible(false);
      }

      var self = this;
      messages.forEach(function (msg) {
        self.appendMessage(msg.role, msg.content);
      });
      this.scrollToBottom();
    },

    showTypingIndicator: function () {
      var wrapper = document.createElement('div');
      wrapper.className = 'chat-message chat-message-assistant';
      wrapper.innerHTML =
        '<div class="message-content typing-indicator">' +
        '<span></span><span></span><span></span>' +
        '</div>';
      this.els.messages.appendChild(wrapper);
      this.scrollToBottom();
      return wrapper;
    },

    scrollToBottom: function () {
      if (this.els.messages) {
        this.els.messages.scrollTop = this.els.messages.scrollHeight;
      }
    },

    setInputState: function (enabled) {
      if (this.els.input) this.els.input.disabled = !enabled;
      if (this.els.sendBtn) this.els.sendBtn.disabled = !enabled;
    },
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Extract the product handle from the current URL.
   * Handles /products/some-handle and /products/some-handle?variant=123
   */
  function getProductHandle() {
    var container = document.querySelector('.product-chat-container');
    if (container && container.dataset.productHandle) return container.dataset.productHandle;

    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function getProductId() {
    var container = document.querySelector('.product-chat-container');
    if (container && container.dataset.productId) return container.dataset.productId;

    // Try to find product ID from Shopify's embedded product JSON
    try {
      var scripts = document.querySelectorAll('script[type="application/json"]');
      for (var i = 0; i < scripts.length; i++) {
        var parsed = JSON.parse(scripts[i].textContent);
        if (parsed && parsed.id && parsed.handle) {
          return String(parsed.id);
        }
      }
    } catch (_) { /* ignore */ }

    // Try meta tag
    try {
      var meta = document.querySelector('meta[property="og:image"]');
      if (meta) {
        var idMatch = meta.content.match(/\/products\/(\d+)\//);
        if (idMatch) return idMatch[1];
      }
    } catch (_) { /* ignore */ }

    return '';
  }

  /**
   * Convert plain text (with markdown-style formatting) to simple HTML.
   * Handles: bold, paragraphs, bullet lists, and numbered lists.
   * Keeps output lightweight — no full markdown parser needed.
   */
  function formatResponse(text) {
    if (!text) return '';
    // AI responses are HTML — render directly
    if (/<[a-z][\s\S]*>/i.test(text)) {
      return text
        // Strip empty paragraphs (<p></p>, <p> </p>, <p>&nbsp;</p>)
        .replace(/<p>\s*(?:&nbsp;)?\s*<\/p>/gi, '')
        // Collapse consecutive <br> tags to a single <br>
        .replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br>')
        // Trim <br>s at paragraph boundaries (already have margin)
        .replace(/<\/p>\s*(?:<br\s*\/?>\s*)+/gi, '</p>')
        .replace(/(?:<br\s*\/?>\s*)+<p>/gi, '<p>');
    }
    // Fallback for plain text: wrap in paragraph and convert newlines
    return '<p>' + text.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>') + '</p>';
  }

  // ---------------------------------------------------------------------------
  // Initialize
  // ---------------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      if (document.querySelector('.product-chat-container')) {
        ChatWidget.init();
      }
    });
  } else {
    // DOM already loaded (e.g. script loaded with defer or at bottom of body)
    if (document.querySelector('.product-chat-container')) {
      ChatWidget.init();
    }
  }
})();
